/* eslint-disable no-unused-vars */
import React, { useMemo, useState } from 'react';
import {
  Card,
  CardContent,
  Grid,
  Typography,
  TextField,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  CardActions,
  Collapse,
  Divider,
  CardHeader,
  CircularProgress,
  Chip,
  Checkbox
} from '@material-ui/core';
import Api from 'src/services/ApiService';

import {
  PurchaseOrderDetails,
  PurchaseOrderItem
} from 'src/redux/slices/purchase-order/types';
import { cloneDeep, debounce, uniq } from 'lodash';
import { slices, useAppDispatch } from 'src/redux';
import { unwrapResult } from '@reduxjs/toolkit';
import {
  CreateListingProduct,
  GetInventoryRequest,
  Inventory,
  Product
} from 'src/types';
import { ListingPriceTypeEnum } from 'src/enums';
import { usePermissions, useSnackBar } from 'src/hooks';
import {
  formatCurrency,
  formatDate,
  getRandomUUIDv4Short,
  sleep
} from 'src/utils';

import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import DeleteForeverIcon from '@material-ui/icons/DeleteForever';
import SaveIcon from '@material-ui/icons/Save';
import { useBranchInfo } from 'src/hooks/branch/use-branch-info';
import { DeleteDialog } from 'src/components';
import { Alert } from '@material-ui/lab';

const { actions: productActions } = slices.product;
const { actions: listingActions } = slices.listing;

const ProductInItemCard = ({
  purchaseOrderProductData,
  poDetails,
  fetchPODetails
}: {
  purchaseOrderProductData?: PurchaseOrderItem;
  poDetails?: PurchaseOrderDetails;
  fetchPODetails?: () => void;
}) => {
  const snackBar = useSnackBar();
  const dispatch = useAppDispatch();
  const { branches } = useBranchInfo();
  const { canCreateProductInPO } = usePermissions();

  const [isSaveLoading, setIsSaveLoading] = useState<boolean>(false);
  const [serialNos, setSerialNos] = useState<string[]>([]);
  const [auditSerialNos, setAuditSerialNos] = useState<Inventory[]>([]);
  const [isAuditsLoading, setIsAuditsLoading] = useState<boolean>(false);

  const [isCollapsibleOpen, setIsCollapsibleOpen] = useState<boolean>(false);
  const [isAuditsOpen, setIsAuditsOpen] = useState<boolean>(false);
  const [serialValue, setSerialValue] = useState<string>();
  const [isDeleteDialogVisible, setIsDeleteDialogVisible] = useState<boolean>(
    false
  );
  const [selectedSerials, setSelectedSerials] = useState<Set<string>>(
    new Set()
  );

  //check if ram kit multiply the listings twice
  const generatePOQty = useMemo(() => {
    const ramKitCount = purchaseOrderProductData?.ram_kit === 2 ? 2 : 1;

    return purchaseOrderProductData && purchaseOrderProductData?.quantity
      ? purchaseOrderProductData?.quantity * ramKitCount || 0
      : 1;
  }, [purchaseOrderProductData]);

  const generateSnQty = useMemo(() => {
    const isDualRamKit = purchaseOrderProductData?.ram_kit === 2;

    if (!serialNos) return 0;

    return isDualRamKit ? Math.floor(serialNos.length / 2) : serialNos.length;
  }, [purchaseOrderProductData, serialNos]);

  const onDeleteSerialNo = (index: number) => {
    const updatedSerialNos = cloneDeep(serialNos);
    updatedSerialNos.splice(index, 1);
    setSerialNos(updatedSerialNos);
  };

  const toggleSerial = (serial: string) => {
    setSelectedSerials((prev) => {
      const updated = new Set(prev);
      if (updated.has(serial)) {
        updated.delete(serial);
      } else {
        updated.add(serial);
      }
      return updated;
    });
  };

  const toggleSelectAll = () => {
    setSelectedSerials((prev) => {
      if (prev.size === serialNos.length) return new Set(); // deselect all
      return new Set(serialNos); // select all
    });
  };

  const generateMultipleNonSNs = async () => {
    const nonSNArray: string[] = [];
    const timeStampNow = +new Date();

    for (let i = 0; i < (generatePOQty || 0); i++) {
      const nonSN = `PCWSN-${+timeStampNow +
        getRandomUUIDv4Short() +
        i}`?.toUpperCase();
      nonSNArray.push(nonSN);
    }
    setSerialNos(nonSNArray);
  };

  // Example usage:
  // console.log(generateRandomSN(8));

  //TODO: depracted after applying the new API, will remove if no bug exist
  const addListingPayloadGenerator = async () => {
    //  DAPAT KAYA NETO MAGRETURN NG
    const productInfo = await fetchProductInfo();
    if (!productInfo) {
      alert('No Product Info? Bawal yan aa');
      return;
    }

    const listings = serialNos.map((sn) => {
      return {
        branch_id: poDetails?.branch?.id,
        branch_name: poDetails?.branch?.name,
        category_id: productInfo?.category_id,
        dealers_price: productInfo?.dealers_price || 0, // Impossible value
        dr_no: poDetails?.dr_no,
        price_type: ListingPriceTypeEnum.NotFree,
        product_id: productInfo?.id,
        purchase_date: poDetails?.po_date || undefined,
        retail_price: productInfo?.retail_price || 0, // Impossible value
        serial_no: sn?.toUpperCase(),
        supplier: poDetails?.supplier?.name,
        supplier_id: poDetails?.supplier?.id,
        warranty_duration: productInfo?.warranty_duration || 0
      };
    });

    const payload = {
      update_main_product_srp: false,
      update_main_product_dp: false,
      multipleListings: listings
    };

    return payload;
  };

  const onCreateListingsOnProduct = debounce(async () => {
    // TODO: Add ref loader
    let payload = {};
    setIsSaveLoading(true);
    if (isSaveLoading) {
      return;
    }

    const hasDuplicatesSerialNos = uniq(serialNos).length !== serialNos.length;

    if (hasDuplicatesSerialNos) {
      snackBar.show({
        severity: 'error',
        message: `There is a duplicate serial number created in your listing!`,
        useSound: true
      });
      setIsSaveLoading(false);
      return;
    }

    if (serialNos.length !== generatePOQty) {
      snackBar.show({
        severity: 'error',
        message: `You need to generate alteast (${purchaseOrderProductData?.quantity}) serial numbers before creating listings of this products`,
        useSound: true
      });
      setIsSaveLoading(false);
      return;
    }

    payload = {
      po_id: poDetails?.po_id || undefined,
      product_id: purchaseOrderProductData?.product_id || undefined,
      price_type: 1 || undefined,
      dr_no: poDetails?.dr_no || undefined,
      serial_nos:
        serialNos.map((sn) => ({
          serial_no: sn?.toUpperCase(),
          status: selectedSerials.has(sn) ? 'on hold' : 'available'
        })) || []
    };

    if (
      purchaseOrderProductData?.amount &&
      purchaseOrderProductData?.amount <= 0 &&
      purchaseOrderProductData?.amount !== undefined
    ) {
      payload = { ...payload, is_free: 1 };
    }

    if (!payload) {
      setIsSaveLoading(false);
      return;
    }

    const response = unwrapResult(
      await dispatch(listingActions.createListingFromPOThunk(payload))
    );

    if (!response?.success) {
      snackBar.show({
        severity: 'error',
        message: response?.message || 'Failed to add listings!',
        useSound: true
      });
    }

    if (response?.errors && Object.keys(response.errors).length > 0) {
      const errors = Object.keys(response.errors);
      snackBar.show({
        severity: 'error',
        message:
          response.errors[errors[0]] ||
          response.message ||
          'Failed to add listings!',
        useSound: true
      });
    }
    if (response?.success && response?.message) {
      if (response?.originalData?.existingListing) {
        const existingListing = response.originalData.existingListing.filter(
          (o1: CreateListingProduct) =>
            serialNos.filter((o2) => o1.serial_no === o2)
        );
        // intended `any[]` type for `existingListing` to avoid type errors
        const existingSerialNos: any[] = existingListing
          .map((item: CreateListingProduct) => item.serial_no)
          ?.filter((item) => item !== undefined);
        snackBar.show({
          severity: 'error',
          message: response?.message || 'Some listings already exists!',
          useSound: true
        });
        setSerialNos(existingSerialNos);
        setIsSaveLoading(false);
        return;
      }

      snackBar.show({
        severity: 'success',
        message: response?.message || 'Successfully added all listings!',
        useSound: true
      });

      if (response?.originalData?.data?.status === 'done_product_in' && fetchPODetails) {
        fetchPODetails();
      }

      setSerialNos([]); // Clear the serial numbers after successfully saving
      setSelectedSerials(new Set()); // Clear the serial numbers after successfully saving
    }

    setIsSaveLoading(false);
  }, 2000);

  const fetchProductInfo = async () => {
    if (!purchaseOrderProductData?.product_id) {
      alert('No Product ID? Bawal yan aa');
      return;
    }
    const response = unwrapResult(
      await dispatch(
        productActions.getProductsViaIdThunk({
          id: purchaseOrderProductData?.product_id
        })
      )
    );
    if (response?.success && response?.originalData?.product) {
      return response?.originalData?.product;
    }
  };

  const singleSNValidation = async (value: string) => {
    if (!value) {
      return {
        isValid: false,
        message: 'Serial number cannot be empty!'
      };
    }

    const res = unwrapResult(
      await dispatch(listingActions.isSerialNoExist(value))
    );

    if (res.success) {
      if (res.originalData.is_exist) {
        return {
          isValid: false,
          message: `${value} serial already exists`
        };
      }
    } else {
      return {
        isValid: false,
        message: `Failed to check serial validity for ${value}`
      };
    }

    if ((generatePOQty || 0) <= serialNos.length) {
      return {
        isValid: false,
        message: 'Quantity cannot surpass the POs Quantity!'
      };
    }

    const isDuplicate = serialNos.some((sn) => sn === value.toUpperCase());
    if (isDuplicate) {
      return {
        isValid: false,
        message: 'Serial number already exists in the current list!'
      };
    }

    return {
      isValid: true,
      message: ''
    };
  };

  const onAddSerialNumber = async (e: any) => {
    const value: string = e.target.value;
    const validation = await singleSNValidation(value);

    if (!validation.isValid) {
      snackBar.show({
        severity: 'error',
        message: validation.message,
        useSound: true
      });
      if (e && e?.target && e?.target?.value) {
        e.target.value = '';
      }
      setSerialValue('');
      return;
    }

    setSerialNos((prev) => [...prev, value?.toUpperCase()]);
    if (e && e?.target && e?.target?.value) {
      e.target.value = '';
    }
    snackBar.show({
      severity: 'success',
      message: `${value} Serial number added!`,
      useSound: true
    });

    setSerialValue('');
  };

  const getInventoryListOfProduct = async () => {
    if (isAuditsOpen) {
      setIsAuditsOpen(false);
      return;
    }

    // TODO: Later create thunk and api for this on purchase order or product in module
    const requestPayload: GetInventoryRequest = {
      product_name: purchaseOrderProductData?.product_name,
      supplier_name: poDetails?.supplier?.name,
      dr_no: poDetails?.dr_no,
      category_id: purchaseOrderProductData?.category?.id,
      branch_ids: branches.map((branch) => branch.id),
      limit: 9999999,
      page: 1,
      action: 'list'
    };
    setIsAuditsLoading(true);
    const inventoryResponse = await Api.post(
      '/inventory/get',
      requestPayload
    ).finally(() => {
      setIsAuditsLoading(false);
    });
    if (inventoryResponse?.success && inventoryResponse?.originalData?.items) {
      // inventoryResponse?.originalData?.data
      const inventoryList: Inventory[] = inventoryResponse?.originalData?.items;
      const filteredInventoryList = inventoryList.filter(
        (item) =>
          item?.serial_no &&
          item?.product_name?.toUpperCase() ===
            purchaseOrderProductData?.product_name?.toUpperCase() &&
          item?.supplier_name?.toUpperCase() ===
            poDetails?.supplier?.name?.toUpperCase() &&
          item?.dr_no?.toUpperCase() === poDetails?.dr_no?.toUpperCase() &&
          item?.category_id === purchaseOrderProductData?.category?.id
      );

      if (
        purchaseOrderProductData?.amount &&
        purchaseOrderProductData?.amount <= 0
      ) {
        const freeSnItem = filteredInventoryList?.filter(
          (item) =>
            typeof item?.dealers_price === 'number' && item.dealers_price <= 0
        );
        setAuditSerialNos(freeSnItem);
        setIsCollapsibleOpen(false);
        return;
      } else {
        const filteredRemoveDpPriceZeroSn = filteredInventoryList?.filter(
          (item) =>
            typeof item?.dealers_price === 'number' && item.dealers_price > 0
        );
        setAuditSerialNos(filteredRemoveDpPriceZeroSn);
        // setIsAuditsOpen((prev) => !prev);
        setIsCollapsibleOpen(false);
      }
    }
  };

  return (
    <div>
      <Card style={{ marginTop: 10 }}>
        <CardHeader
          title={
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center'
              }}
            >
              <Typography variant="h6" style={{ fontWeight: 'bold' }}>
                {purchaseOrderProductData?.product_name}
              </Typography>
              {purchaseOrderProductData?.ram_kit === 2 ? (
                <Chip
                  style={{ marginLeft: 5 }}
                  label={
                    <Typography style={{}} variant="subtitle2">
                      {purchaseOrderProductData?.ram_kit &&
                      purchaseOrderProductData?.ram_kit === 2
                        ? 'DUAL RAM KIT'
                        : null}
                    </Typography>
                  }
                  size="small"
                  variant="outlined"
                  color="primary"
                />
              ) : null}
              {purchaseOrderProductData?.amount &&
              purchaseOrderProductData?.amount <= 0 ? (
                <Chip
                  style={{ marginLeft: 5 }}
                  label={
                    <Typography style={{}} variant="subtitle2">
                      {'FREE ITEM'}
                    </Typography>
                  }
                  size="small"
                  variant="outlined"
                  color="primary"
                />
              ) : null}
            </div>
          }
          subheader={
            <Typography variant="body2" color="textSecondary">
              {purchaseOrderProductData?.sku}
            </Typography>
          }
          action={
            <Button
              fullWidth
              variant="contained"
              color="primary"
              startIcon={
                isSaveLoading ? <CircularProgress size={24} /> : <SaveIcon />
              }
              disabled={
                isSaveLoading ||
                serialNos?.length === 0 ||
                !canCreateProductInPO
              }
              onClick={() => {
                setIsSaveLoading(true);
                onCreateListingsOnProduct();
              }}
            >
              {`Save SN(s)`}
            </Button>
          }
        />
        <Divider />

        <CardContent>
          <Grid container spacing={2}>
            {/* Left side: Product info */}
            <Grid item xs={12} md={4}>
              <Typography variant="body2">
                PO Qty: {purchaseOrderProductData?.quantity}
              </Typography>
              <Typography variant="body2">
                SN Qty: {generateSnQty || 0}
              </Typography>
            </Grid>

            {/* Right side: Input fields */}
            <Grid item xs={12} md={8} style={{ backgroundColor: '#f5f5f5' }}>
              <TextField
                value={serialValue}
                onChange={(e) => setSerialValue(e.target.value)}
                variant="outlined"
                label="Enter SN"
                fullWidth
                size="small"
                onKeyDown={(e: any) => {
                  if (e.key === 'Enter') {
                    onAddSerialNumber(e);
                  }
                }}
              />
              <Typography
                style={{ margin: 4, textAlign: 'center', fontWeight: 'bold' }}
              >
                OR
              </Typography>
              <Button
                fullWidth
                variant="outlined"
                color="primary"
                size="small"
                onClick={generateMultipleNonSNs}
              >
                Generate SN
              </Button>
            </Grid>
          </Grid>
        </CardContent>
        <Divider />
        <CardActions disableSpacing style={{ padding: 0 }}>
          <Button
            color="primary"
            variant="text"
            size="small"
            fullWidth
            startIcon={
              isCollapsibleOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />
            }
            onClick={() => {
              serialNos?.[0] && setIsCollapsibleOpen(!isCollapsibleOpen);
              setIsAuditsOpen(false);
            }}
          >
            Serial No(s) Added: {serialNos?.length || 0}
          </Button>

          <Button
            color="primary"
            variant="outlined"
            size="small"
            fullWidth
            startIcon={isAuditsOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            onClick={() => {
              setIsAuditsOpen(true);
              getInventoryListOfProduct();
            }}
          >
            Audit SNs ({auditSerialNos?.length || 0})
          </Button>
        </CardActions>
      </Card>

      <Collapse in={isCollapsibleOpen} timeout={0} unmountOnExit>
        <div
          style={{
            border: '1px solid #ccc',
            borderRadius: 4,
            marginTop: 10
          }}
        >
          {serialNos.length > 0 ? (
            <Button
              color="secondary"
              variant="text"
              size="small"
              fullWidth
              onClick={() => setIsDeleteDialogVisible(true)}
            >
              Clear All Serial No(s)
            </Button>
          ) : null}
          {purchaseOrderProductData?.ram_kit === 2 ? (
            <Alert severity={'info'}>
              {
                'This product is a dual RAM kit; serial numbers must be generate twice to consider it as one quantity.'
              }
            </Alert>
          ) : null}
          <List dense style={{ marginTop: 10, padding: 2 }}>
            {serialNos?.[0] ? (
              <ListItem
                id="list-toolbar"
                style={{
                  display: 'flex',
                  justifyContent: 'end',
                  gap: '1em',
                  height: '3em',
                  paddingBottom: '1em'
                }}
              >
                <ListItemIcon>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={toggleSelectAll}
                  >
                    {selectedSerials.size === serialNos.length
                      ? 'Select None'
                      : 'Select All'}
                  </Button>
                </ListItemIcon>
                <Typography
                  color="primary"
                  style={{
                    fontWeight: 'bold',
                    fontSize: '1.2em'
                  }}
                >
                  ON HOLD
                </Typography>
              </ListItem>
            ) : null}

            {serialNos.map((serialNo, index) => (
              <ListItem
                id={`serial-no-${index}`}
                key={index}
                divider
                style={{
                  backgroundColor: index % 2 === 0 ? '#f5f5f5' : '#fff'
                }}
              >
                <ListItemIcon>
                  <IconButton
                    size="small"
                    onClick={() => onDeleteSerialNo(index)}
                  >
                    <DeleteForeverIcon color="secondary" fontSize="small" />
                  </IconButton>
                </ListItemIcon>
                <ListItemText primary={serialNo} />
                <ListItemIcon>
                  <IconButton size="small" style={{ marginRight: '1em' }}>
                    <Checkbox
                      color="primary"
                      checked={selectedSerials.has(serialNo)}
                      onClick={() => toggleSerial(serialNo)}
                    />
                  </IconButton>
                </ListItemIcon>
              </ListItem>
            ))}
          </List>
        </div>
      </Collapse>

      <Collapse in={isAuditsOpen} timeout={0} unmountOnExit>
        <List dense style={{ marginTop: 10, padding: 2 }}>
          {isAuditsLoading && <CircularProgress size={20} />}
          {auditSerialNos?.map((inventory, index) => (
            <ListItem
              key={index}
              divider
              style={{
                backgroundColor: index % 2 === 0 ? '#f5f5f5' : '#fff'
              }}
            >
              <ListItemText
                primary={
                  <Typography variant="h6">{`${inventory?.serial_no} (${inventory?.status})`}</Typography>
                }
                secondary={`Encoded ${inventory?.date_encoded} by ${inventory?.encoded_by} · ${inventory?.branch_name}`}
              />
            </ListItem>
          ))}
        </List>
      </Collapse>

      <DeleteDialog
        title="Deleting Serial No(s)"
        subTitle={`Are you sure you want to delete all Serial No(s)?`}
        isVisible={isDeleteDialogVisible}
        handleClose={() => setIsDeleteDialogVisible(false)}
        onDelete={() => {
          setSerialNos([]);
          setIsCollapsibleOpen(false);
          setSelectedSerials(new Set());
          setIsDeleteDialogVisible(false);
        }}
      />
    </div>
  );
};

export default ProductInItemCard;
